import { PLAYER } from '@/config/player';
import { t, type I18nKey } from '@/i18n';
import type { RunState } from '@/run/run-state';
import { bagCap, bagCount } from '@/run/ship';
import type { World } from '@/scene/world';
import type { Hud } from './hud';
import { syncObjective } from './objective-arrow';

/** Reused bag read-out (no per-frame allocation). */
const bag = { count: 0, cap: 0, fresh: 0, crate: false };

/** Push the frame's ship / economy / boss read-outs into the HUD. */
export function syncHud(
  hud: Hud, world: World, run: RunState, pilotLevel: number, screenW: number,
): void {
  const { player, boss } = world;
  syncObjective(hud.view, world);
  hud.setHp(player.hp, player.maxHp, player.armor);
  hud.setEnergy(player.energy / PLAYER.maxEnergy, player.dashReadyPct);
  bag.count = bagCount(player, run);
  bag.cap = bagCap(player);
  bag.fresh = run.freshWeapons.length;
  bag.crate = run.pendingWeapon !== null;
  hud.setWeapons(player.weapon, bag);
  hud.setGems(run.gems, run.red);
  hud.setLevel(pilotLevel);
  const sector = run.sector;
  hud.setSector(sector ? run.sectorIndex + 1 : 0, run.roomInSector + 1, sector ? sector.rooms + 1 : 0);
  const wave = run.wave;
  if (boss && !boss.dead && wave.bossHeld) {
    // Wave phase (C8): the bar tracks the CURRENT WAVE — the boss's name
    // stays hidden until it actually enters (it confused players before)
    hud.setBoss(
      t('hud.wave', { n: Math.min(wave.index + 1, wave.total), m: wave.total }),
      wave.spawned > 0 ? world.minions.count / wave.spawned : 0,
      screenW,
    );
  } else {
    hud.setBoss(
      boss && !boss.dead ? `${t(boss.def.nameKey as I18nKey)} Lv.${boss.level}` : null,
      boss?.hpPct ?? 0,
      screenW,
    );
  }
}
